import type { DataChartPayload } from '../../core/types/payloads/data_chart.types';

const CHART_COLORS = ['#2563eb', '#16a34a', '#ea580c', '#9333ea', '#0891b2', '#dc2626', '#ca8a04', '#4b5563'];

function maxValue(data: DataChartPayload['data']): number {
  return Math.max(...data.map((point) => point.value), 1);
}

function DataTable({ payload }: { payload: DataChartPayload }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="border border-[var(--sala-border,#d1d5db)] bg-[var(--sala-surface,#f9fafb)] px-3 py-2 text-left">
            {payload.xAxisLabel || 'Categoría'}
          </th>
          <th className="border border-[var(--sala-border,#d1d5db)] bg-[var(--sala-surface,#f9fafb)] px-3 py-2 text-right">
            {payload.yAxisLabel || 'Valor'}
          </th>
        </tr>
      </thead>
      <tbody>
        {payload.data.map((point) => (
          <tr key={point.label}>
            <td className="border border-[var(--sala-border,#d1d5db)] px-3 py-2">{point.label}</td>
            <td className="border border-[var(--sala-border,#d1d5db)] px-3 py-2 text-right tabular-nums">
              {point.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BarChart({ payload }: { payload: DataChartPayload }) {
  const ceiling = maxValue(payload.data);
  return (
    <div className="space-y-3">
      <div className="flex h-48 items-end gap-3 border-b border-l border-[var(--sala-border,#d1d5db)] px-2 pb-2 pl-8">
        {payload.data.map((point, index) => {
          const height = `${Math.max(8, (point.value / ceiling) * 100)}%`;
          return (
            <div key={point.label} className="flex min-w-0 flex-1 flex-col items-center gap-2">
              <span className="text-xs font-medium tabular-nums">{point.value}</span>
              <div
                className="w-full max-w-12 rounded-t-md"
                style={{ height, backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                aria-hidden
              />
              <span className="max-w-full truncate text-center text-xs">{point.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-[var(--sala-ink-dim,#6b7280)]">
        <span>{payload.xAxisLabel || 'Eje X'}</span>
        <span>{payload.yAxisLabel || 'Eje Y'}</span>
      </div>
    </div>
  );
}

function DotChart({ payload }: { payload: DataChartPayload }) {
  const ceiling = maxValue(payload.data);
  const width = 320;
  const height = 180;
  const padding = 24;
  const step = payload.data.length > 1 ? (width - padding * 2) / (payload.data.length - 1) : 0;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full max-w-md" role="img" aria-label={payload.title}>
      <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="currentColor" strokeWidth="1.5" />
      <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="currentColor" strokeWidth="1.5" />
      {payload.data.map((point, index) => {
        const x = padding + step * index;
        const y = height - padding - (point.value / ceiling) * (height - padding * 2);
        return (
          <g key={point.label}>
            <circle cx={x} cy={y} r="5" fill={CHART_COLORS[index % CHART_COLORS.length]} />
            <text x={x} y={height - 6} textAnchor="middle" fontSize="10">
              {point.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ payload }: { payload: DataChartPayload }) {
  const ceiling = maxValue(payload.data);
  const width = 320;
  const height = 180;
  const padding = 24;
  const step = payload.data.length > 1 ? (width - padding * 2) / (payload.data.length - 1) : 0;
  const points = payload.data
    .map((point, index) => {
      const x = padding + step * index;
      const y = height - padding - (point.value / ceiling) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full max-w-md" role="img" aria-label={payload.title}>
      <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="currentColor" strokeWidth="1.5" />
      <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="currentColor" strokeWidth="1.5" />
      <polyline fill="none" stroke="#2563eb" strokeWidth="2.5" points={points} />
      {payload.data.map((point, index) => {
        const x = padding + step * index;
        const y = height - padding - (point.value / ceiling) * (height - padding * 2);
        return (
          <g key={point.label}>
            <circle cx={x} cy={y} r="4" fill="#2563eb" />
            <text x={x} y={height - 6} textAnchor="middle" fontSize="10">
              {point.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChart({ payload }: { payload: DataChartPayload }) {
  const total = payload.data.reduce((sum, point) => sum + point.value, 0) || 1;
  let cursor = 0;
  const radius = 72;
  const center = 90;

  const slices = payload.data.map((point, index) => {
    const start = cursor;
    const angle = (point.value / total) * Math.PI * 2;
    cursor += angle;
    const end = cursor;
    const x1 = center + radius * Math.cos(start - Math.PI / 2);
    const y1 = center + radius * Math.sin(start - Math.PI / 2);
    const x2 = center + radius * Math.cos(end - Math.PI / 2);
    const y2 = center + radius * Math.sin(end - Math.PI / 2);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    return { path, color: CHART_COLORS[index % CHART_COLORS.length], label: point.label, value: point.value };
  });

  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg viewBox="0 0 180 180" className="h-44 w-44 shrink-0" role="img" aria-label={payload.title}>
        {slices.map((slice) => (
          <path key={slice.label} d={slice.path} fill={slice.color} />
        ))}
      </svg>
      <ul className="space-y-1 text-sm">
        {slices.map((slice) => (
          <li key={slice.label} className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: slice.color }} aria-hidden />
            <span>
              {slice.label}: {slice.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DataChartView({ payload }: { payload: DataChartPayload }) {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">{payload.title}</h3>
      {payload.chartType === 'table' && <DataTable payload={payload} />}
      {payload.chartType === 'bar' && <BarChart payload={payload} />}
      {payload.chartType === 'line' && <LineChart payload={payload} />}
      {payload.chartType === 'dot' && <DotChart payload={payload} />}
      {payload.chartType === 'pie' && <PieChart payload={payload} />}
    </div>
  );
}
